import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Rating,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CardActionArea,
  CardMedia,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  TrendingUp,
  Phone,
  Language,
  Visibility,
  RateReview,
  PhotoCamera,
  PostAdd,
  DateRange,
  AccessTime,
  ShowChart
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useBusiness } from '../../contexts/BusinessContext';
import axios from 'axios';
import LocationMap from '../ranking/LocationMap';
import ServiceActivityLog from './ServiceActivityLog';

// Simple Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Map Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>Map failed to load</Box>;
    }

    return this.props.children;
  }
}

const GMBActivity = () => {
  const { selectedBusiness: contextBusiness } = useBusiness();
  const location = useLocation();

  // Use business from location.state (from TopPerformerPage) if available, otherwise use context
  const businessFromState = location.state?.business;
  const selectedBusiness = businessFromState || contextBusiness;

  console.log('📊 GMBActivity - Business selection:', {
    fromState: !!businessFromState,
    fromContext: !!contextBusiness,
    selectedBusinessName: selectedBusiness?.businessName || selectedBusiness?.title,
    googleLocationId: selectedBusiness?.googleLocationId,
    contextBusinessId: contextBusiness?.id
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    businessName:'',
    posts: [],
    medias: [],
    reviews: [],
    serviceActivities: [],
    activity:{},
          insights:{},
    stats: {
      impressions: 0,
      calls: 0,
      websiteClicks: 0
    }
  });

  // Initialize dateRange from location.state or use default (last 12 months)
  const initializeDateRange = () => {
    const selectedTimePeriod = location.state?.selectedTimePeriod;

    if (selectedTimePeriod && selectedTimePeriod.start && selectedTimePeriod.end) {
      // Convert from {year, month, day} format to Date objects
      const startDate = new Date(
        selectedTimePeriod.start.year,
        selectedTimePeriod.start.month - 1,
        selectedTimePeriod.start.day
      );
      const endDate = new Date(
        selectedTimePeriod.end.year,
        selectedTimePeriod.end.month - 1,
        selectedTimePeriod.end.day
      );
      return { startDate, endDate };
    }

    // Default: last 12 months
    return {
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 12)),
      endDate: new Date()
    };
  };

  const [dateRange, setDateRange] = useState(initializeDateRange());
const [duration, setDuration] = useState('');
// Calculate duration whenever dateRange changes
useEffect(() => {
  const calculateDuration = (start, end) => {
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      // Get the last day of the previous month
      const lastDayOfMonth = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
      days += lastDayOfMonth;
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    // Build the duration string
    const parts = [];
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days > 0 || parts.length === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);

    return parts.join(' ');
  };

  setDuration(calculateDuration(dateRange.startDate, dateRange.endDate));
}, [dateRange.startDate, dateRange.endDate]);




  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null); // Filter by specific date
  const [availableDates, setAvailableDates] = useState([]); // Dates available in current tab
  const [tabDates, setTabDates] = useState({
    reviews: [],
    medias: [],
    posts: [],
    keywords: []
  }); // Dates for each tab
  const [selectedKeyword, setSelectedKeyword] = useState(null); // Filter by specific keyword
  const [availableKeywords, setAvailableKeywords] = useState([]); // Keywords available in data
  const [insightsChartData, setInsightsChartData] = useState([]); // Processed insights chart data
  const [visibleMetrics, setVisibleMetrics] = useState({
    BUSINESS_IMPRESSIONS: true,
    CALL_CLICKS: true,
    WEBSITE_CLICKS: true
  }); // Toggle visibility of metrics on GMB Insights chart

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter data by selected date
  const filterDataByDate = (items, dateField) => {
    if (!selectedDate || !items || items.length === 0) return items;

    return items.filter(item => {
      const itemDate = new Date(item[dateField]).toLocaleDateString('de-DE');
      return itemDate === selectedDate;
    });
  };

  // Filter posts by selected date (posts have multiple date fields)
  const filterPostsByDate = (items) => {
    if (!selectedDate || !items || items.length === 0) return items;

    return items.filter(post => {
      const dateValue = post.publishedAt || post.scheduledFor || post.timestamp || post.createTime;
      if (!dateValue) return false;
      const postDate = new Date(dateValue).toLocaleDateString('de-DE');
      return postDate === selectedDate;
    });
  };

  // Filter keywords by selected keyword
  const filterKeywordsByKeyword = (items) => {
    if (!selectedKeyword || !items || items.length === 0) return items;
    return items.filter(item => item.keyword === selectedKeyword);
  };

  // Process insights data into time series chart format
  const processInsightsData = (insightsResult) => {
    if (!insightsResult || !insightsResult.data || !insightsResult.data.timeSeries) {
      return [];
    }

    const timeSeries = insightsResult.data.timeSeries;
    const dateMap = new Map();

    // Process time series data and aggregate by date
    Object.keys(timeSeries).forEach(metric => {
      const metricData = timeSeries[metric] || [];

      metricData.forEach((dataPoint) => {
        const dateKey = `${dataPoint.date.year}-${String(dataPoint.date.month).padStart(2, '0')}-${String(dataPoint.date.day).padStart(2, '0')}`;

        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            date: dateKey,
            formattedDate: new Date(dataPoint.date.year, dataPoint.date.month - 1, dataPoint.date.day).toLocaleDateString('de-DE'),
            BUSINESS_IMPRESSIONS: 0,
            CALL_CLICKS: 0,
            WEBSITE_CLICKS: 0
          });
        }

        const entry = dateMap.get(dateKey);
        const value = dataPoint.value || 0;

        // Aggregate metrics
        if (metric.includes('BUSINESS_IMPRESSIONS')) {
          entry.BUSINESS_IMPRESSIONS += value;
        } else if (metric === 'CALL_CLICKS') {
          entry.CALL_CLICKS += value;
        } else if (metric === 'WEBSITE_CLICKS') {
          entry.WEBSITE_CLICKS += value;
        }
      });
    });

    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const fetchGMBActivity = useCallback(async (startDate, endDate,selectedBusiness) => {

    // Handle both id (from context) and googleLocationId (from TopPerformerPage)
    const locationId = selectedBusiness?.id || selectedBusiness?.googleLocationId;
    if (!locationId) {
      console.warn('⚠️ No location ID found in selected business');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      
      // Parallel fetch for activity data and insights
      const [activityResponse, insightsResponse, servicesResponse] = await Promise.all([
  axios.post('/api/gmb/activity', {
    locationId,
    startDate: formatDateForAPI(startDate),
    endDate: formatDateForAPI(endDate)
  }, {
    headers: {
      'Content-Type': 'application/json'
    }
  }),



  



  




  axios.post(`/api/businesses/${locationId}/insights`, {
    metrics: [
      'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
      'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
      'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
      'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
      'CALL_CLICKS',
      'WEBSITE_CLICKS'
    ],
    dateRange: {
      startDate: {
        year: startDate.getFullYear(),
        month: startDate.getMonth() + 1,
        day: startDate.getDate()
      },
      endDate: {
        year: endDate.getFullYear(),
        month: endDate.getMonth() + 1,
        day: endDate.getDate()
      }
    }
  }, {
    headers: {
      'Content-Type': 'application/json'
    }
  }),

  axios.get(`/api/activities/${locationId}`, {
    params: { limit: 1000 }
  })
]);
      
      const activityResult = activityResponse.data;
    
      const insightsResult =  insightsResponse.data;
     
      

      
      if (activityResult.success  &&  insightsResult.success) {
       
        let stats = {
          impressions: 0,
          calls: 0,
          websiteClicks: 0
        };
          const insightStats = insightsResult.data.summaryStats;
          
         stats.impressions = 
  insightStats.BUSINESS_IMPRESSIONS_DESKTOP_MAPS.total +
  insightStats.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH.total +
  insightStats.BUSINESS_IMPRESSIONS_MOBILE_MAPS.total +
  insightStats.BUSINESS_IMPRESSIONS_MOBILE_SEARCH.total;

stats.calls = insightStats.CALL_CLICKS.total;
stats.websiteClicks = insightStats.WEBSITE_CLICKS.total;




        
        
        const newData = {
          ...data,
          businessName:insightsResult.data.businessInfo,
          posts: activityResult.data.posts,
          medias: activityResult.data.medias,
          reviews: activityResult.data.reviews,
          keywords:activityResult.data.keywords,
          serviceActivities: servicesResponse.data?.data || [],
          activity:activityResult,
          insights:insightsResult,
          stats:stats
        };

        setData(newData);

        // Helper function to extract unique dates from array
        const extractUniqueDates = (items, dateField) => {
          if (!items || items.length === 0) return [];
          const uniqueDates = new Set();
          items.forEach(item => {
            if (item[dateField]) {
              const dateStr = new Date(item[dateField]).toLocaleDateString('de-DE');
              uniqueDates.add(dateStr);
            }
          });
          return Array.from(uniqueDates).sort((a, b) => {
            const dateA = new Date(a.split('.').reverse().join('-'));
            const dateB = new Date(b.split('.').reverse().join('-'));
            return dateB - dateA; // Newest first
          });
        };

        // Helper function to extract dates from posts (multiple date fields)
        const extractPostDates = (items) => {
          if (!items || items.length === 0) return [];
          const uniqueDates = new Set();
          items.forEach(item => {
            // Posts can have publishedAt, scheduledFor, or timestamp/createTime
            const dateValue = item.publishedAt || item.scheduledFor || item.timestamp || item.createTime;
            if (dateValue) {
              const dateStr = new Date(dateValue).toLocaleDateString('de-DE');
              uniqueDates.add(dateStr);
            }
          });
          return Array.from(uniqueDates).sort((a, b) => {
            const dateA = new Date(a.split('.').reverse().join('-'));
            const dateB = new Date(b.split('.').reverse().join('-'));
            return dateB - dateA; // Newest first
          });
        };

        // Extract unique dates for each tab
        const reviewDates = extractUniqueDates(activityResult.data.reviews, 'time');
        const mediaDates = extractUniqueDates(activityResult.data.medias, 'createTime');
        const postDates = extractPostDates(activityResult.data.posts); // Use special function for posts
        const keywordDates = extractUniqueDates(activityResult.data.keywords, 'analyzedAt');
        const serviceDates = extractUniqueDates(servicesResponse.data?.data || [], 'timestamp');

        console.log('📅 Date Extraction Debug:');
        console.log('  Reviews dates:', reviewDates.length, reviewDates.slice(0, 3));
        console.log('  Medias dates:', mediaDates.length, mediaDates.slice(0, 3));
        console.log('  Posts dates:', postDates.length, postDates.slice(0, 3));
        console.log('  Keywords dates:', keywordDates.length, keywordDates.slice(0, 3));
        console.log('  Services dates:', serviceDates.length, serviceDates.slice(0, 3));
        console.log('  Sample post:', activityResult.data.posts?.[0]);

        // Store dates for each tab
        const newTabDates = {
          reviews: reviewDates,
          medias: mediaDates,
          posts: postDates,
          keywords: keywordDates,
          services: serviceDates
        };

        setTabDates(newTabDates);
        // Set initial available dates based on first tab (Reviews)
        setAvailableDates(reviewDates);
        setSelectedDate(null); // Don't set default - show "Select Date" initially

        // Extract unique keywords for keyword filter
        const uniqueKeywords = new Set();
        if (activityResult.data.keywords && Array.isArray(activityResult.data.keywords)) {
          activityResult.data.keywords.forEach(kw => {
            if (kw.keyword) {
              uniqueKeywords.add(kw.keyword);
            }
          });
        }
        const sortedKeywords = Array.from(uniqueKeywords).sort();
        setAvailableKeywords(sortedKeywords);
        setSelectedKeyword(null); // Don't set default

        // Process insights data for chart
        const chartData = processInsightsData(insightsResult);
        setInsightsChartData(chartData);
      } else {
        throw new Error(activityResult.error || 'Failed to fetch activity data');
      }


     


    } catch (error) {
      setError({
        message: error.message,
        details: 'Unable to load GMB activity data'
      });
      console.error('Fetch Error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    const locationId = selectedBusiness?.id || selectedBusiness?.googleLocationId;
    if (locationId) {
      fetchGMBActivity(dateRange.startDate, dateRange.endDate,selectedBusiness);
    }
  }, [selectedBusiness, fetchGMBActivity]);

  useEffect(() => {
    
      fetchGMBActivity(dateRange.startDate, dateRange.endDate,selectedBusiness);
    
  }, []);


  const handleDateChange = (field, value) => {
    const newDateRange = {
      ...dateRange,
      [field]: new Date(value)
    };
    setDateRange(newDateRange);
  };

  // Reset pagination when filters change (date or keyword)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, selectedKeyword]);

const formatDateToWords = (dateString) => {
  let date;
  
  // Try different date parsing methods
  if (dateString.includes('-')) {
    // Handle ISO format or YYYY-MM-DD
    date = new Date(dateString);
  } else if (dateString.includes('.')) {
    // Handle German format DD.MM.YYYY
    const [day, month, year] = dateString.split('.');
    date = new Date(`${year}-${month}-${day}`);
  } else {
    // Try direct parsing
    date = new Date(dateString);
  }
  
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  
  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return `${date.getDate()} ${monthNames[date.getMonth()]}`;
};

// Shows: 17. Nov. (with German locale)

  const handleApplyDateFilter = () => {
    const locationId = selectedBusiness?.id || selectedBusiness?.googleLocationId;
    if (dateRange.startDate && dateRange.endDate && locationId) {
      fetchGMBActivity(dateRange.startDate, dateRange.endDate,selectedBusiness);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(10);

  const MetricCard = ({ title, value, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const ReviewCard = ({ review }) => {
    const getReviewerAvatar = (reviewer) => {
      if (reviewer?.profilePhotoUrl) {
        return <Avatar src={reviewer.profilePhotoUrl} alt={reviewer.displayName} />;
      }
      return (
        <Avatar sx={{ bgcolor: 'primary.light' }}>
          {reviewer?.displayName?.charAt(0) || review.author_name?.charAt(0) || 'A'}
        </Avatar>
      );
    };

    const getRatingColor = (rating) => {
      if (rating >= 4) return 'success';
      if (rating >= 3) return 'warning';
      return 'error';
    };

    return (
      <Paper key={review.id} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {getReviewerAvatar(review.reviewer)}
          <Box flex={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {review.reviewer?.displayName || review.author.name || 'Anonymer Nutzer'}
                  </Typography>
                  {review.reviewer?.isAnonymous && (
                    <Chip label="Anonym" size="small" variant="outlined" />
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Rating 
                    value={review.rating || review.starRating} 
                    readOnly 
                    size="small"
                    sx={{ color: `${getRatingColor(review.rating || review.starRating)}.main` }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    ({review.rating || review.starRating}/5)
                  </Typography>
                </Box>
                <Typography variant="caption" display="block" color="textSecondary">
                  {formatDate(review.time || review.createTime)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" alignItems="end" gap={1}>
                <Chip 
                  label={review.reply || review.reviewReply ? 'Beantwortet' : 'Keine Antwort'} 
                  color={review.reply || review.reviewReply ? 'success' : 'warning'}
                  size="small"
                />
                <Chip 
                  label={review.reviewType || 'Google'} 
                  variant="outlined" 
                  size="small"
                />
              </Box>
            </Box>
            
            {(review.comment || review.text) && (
              <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line', mt: 2 }}>
                {review.comment || review.text}
              </Typography>
            )}
            
            {(review.response || review.reviewReply) && (
              <Box sx={{ 
                bgcolor: 'success.50', 
                p: 1.5, 
                borderRadius: 1, 
                mt: 2,
                border: '1px solid',
                borderColor: 'success.200'
              }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>
                    <RateReview fontSize="small" />
                  </Avatar>
                  <Typography variant="caption" color="success.main" fontWeight="bold">
                    Antwort des Unternehmens:
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {review.response?.comment || review.reviewReply?.comment}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  Beantwortet am: {formatDate(review.response?.updateTime || review.reviewReply?.updateTime)}
                </Typography>
              </Box>
            )}
            
            <Box display="flex" justifyContent="between" alignItems="center" mt={2}>
              <Typography variant="caption" color="textSecondary">
                {review.timeDescription
}
              </Typography>
              {review.helpful_count && (
                <Typography variant="caption" color="textSecondary">
                  👍 {review.helpful_count} fanden das hilfreich
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const PhotoCard = ({ photo }) => (
    <Paper key={photo.id} sx={{ p: 2, mb: 2 }}>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Avatar sx={{ bgcolor: 'secondary.light' }}>
          <PhotoCamera />
        </Avatar>
        <Box flex={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2">
              Foto hochgeladen
            </Typography>
            <Chip 
              label={photo.photoType || photo.mediaFormat || 'Foto'} 
              color="secondary"
              size="small"
            />
          </Box>
          <Typography variant="caption" color="textSecondary" display="block" mb={2}>
            {formatDate(photo.timestamp || photo.createTime)}
          </Typography>
          <Box mb={2}>
            <img 
              src={photo.sourceUrl}
              alt="GMB Foto"
              style={{ 
                maxWidth: '200px',
                maxHeight: '150px',
                borderRadius: '8px',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
          {photo.description && (
            <Typography variant="body2" color="textSecondary">
              {photo.description}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );

  const PostCard = ({ post }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'published': return 'success';
        case 'scheduled': return 'warning';
        case 'draft': return 'default';
        case 'failed': return 'error';
        default: return 'default';
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 'published': return 'Veröffentlicht';
        case 'scheduled': return 'Geplant';
        case 'draft': return 'Entwurf';
        case 'failed': return 'Fehlgeschlagen';
        default: return status;
      }
    };

    return (
      <Paper key={post.id} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Avatar sx={{ bgcolor: 'info.light' }}>
            <PostAdd />
          </Avatar>
          <Box flex={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2">
                  Business Post
                </Typography>
                {post.title && (
                  <Typography variant="body2" color="primary" fontWeight="bold">
                    {post.title}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1}>
                <Chip 
                  label={getStatusText(post.status)} 
                  color={getStatusColor(post.status)}
                  size="small"
                />
                {post.topicType && (
                  <Chip 
                    label={post.topicType} 
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
            
            <Typography variant="caption" color="textSecondary" display="block" mb={2}>
              {post.status === 'published' && post.publishedAt ? 
                `Veröffentlicht: ${formatDate(post.publishedAt)}` :
                post.status === 'scheduled' && post.scheduledFor ? 
                `Geplant für: ${formatDate(post.scheduledFor)}` :
                `Erstellt: ${formatDate(post.timestamp || post.createTime)}`
              }
            </Typography>
            
            {post.content && (
              <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line' }}>
                {post.content}
              </Typography>
            )}
            
            {post.summary && !post.content && (
              <Typography variant="body2" paragraph>
                {post.summary}
              </Typography>
            )}
            
            {post.scheduledFor && post.status === 'scheduled' && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DateRange fontSize="small" color="warning" />
                <Typography variant="body2" color="warning.main">
                  Geplant für: {formatDate(post.scheduledFor)}
                </Typography>
              </Box>
            )}
            
            {post?.media && post.media?.length > 0 && (
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  <strong>Medien: </strong>{0} Element(e)
                </Typography>
                {post.media[0]?.sourceUrl && (
                  <Box>
                    <img 
                      src={post.media[0].sourceUrl}
                      alt="Post Bild"
                      style={{ 
                        maxWidth: '200px',
                        maxHeight: '150px',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
            
            {post.engagement && (post.engagement.views > 0 || post.engagement.clicks > 0) && (
              <Box display="flex" gap={2} mt={2}>
                <Typography variant="caption" color="textSecondary">
                  👁️ {post.engagement.views} Aufrufe
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  👆 {post.engagement.clicks} Klicks
                </Typography>
              </Box>
            )}
            
            {post.googlePostId && (
              <Typography variant="caption" color="success.main" display="block" mt={1}>
                ✅ Mit Google My Business verknüpft
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    // Update available dates based on selected tab
    // Tab order: 0=GMB Insights, 1=Reviews, 2=Medias, 3=Posts, 4=Services, 5=Keywords, 6=Keyword Ranking Maps
    const tabNames = ['insights', 'reviews', 'medias', 'posts', 'services', 'keywords', 'keywords'];
    const currentTabName = tabNames[newValue];
    const newAvailableDates = tabDates[currentTabName] || [];

    // For GMB Insights tab (tab 0), don't show date filter
    if (newValue === 0) {
      setAvailableDates([]);
    } else {
      setAvailableDates(newAvailableDates);
    }

    // Reset selected date if not available in new tab (except for GMB Insights)
    if (selectedDate && !newAvailableDates.includes(selectedDate) && newValue !== 0) {
      setSelectedDate(null);
    }

    // Reset pagination when switching tabs
    setCurrentPage(1);
  };

  if (!selectedBusiness) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="info">
          Bitte wählen Sie ein Unternehmen aus, um die Aktivitätsdaten anzuzeigen.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Aktivitäten
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          {selectedBusiness.businessName}
        </Typography>
        
        {/* Date Range Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              label="Von"
              type="date"
              value={formatDateForInput(dateRange.startDate)}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              size="small"
            />
            <TextField
              label="Bis"
              type="date"
              value={formatDateForInput(dateRange.endDate)}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleApplyDateFilter}
              disabled={loading}
              startIcon={<DateRange />}
            >
              Filter anwenden
            </Button>
           <Typography 
  variant="body2" 
  sx={{ 
    color: 'white',
    fontWeight: 'bold',
    backgroundColor: 'primary.main',
    px: 2,
    py: 1,
    borderRadius: 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
    }
  }}
>
  <AccessTime sx={{ fontSize: '1.1rem' }} />
  {duration}
</Typography>
          </Box>
        </Paper>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message}
          {error.details && <Box>{error.details}</Box>}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Lade GMB Aktivitätsdaten...
          </Typography>
        </Box>
      )}

      {/* Metrics Cards */
      
      console.log('----data',data)}
      {!loading && data && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Impressionen"
              value={data.stats.impressions || 0}
              icon={<Visibility />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Anrufe"
              value={data.stats?.calls || 0}
              icon={<Phone />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Website-Klicks"
              value={data.stats?.websiteClicks || 0}
              icon={<Language />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Bewertungen"
              value={data.reviews.length|| 0
              }
              icon={<RateReview />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Medias"
              value={data.medias.length|| 0
              }
              icon={<RateReview />}
              color="error"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Posts"
              value={data.posts.length|| 0
              }
              icon={<RateReview />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Services"
              value={data?.serviceActivities?.length || 0}
              icon={<RateReview />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MetricCard
              title="Keywords"
              value={data?.keywords?.length || 0}
              icon={<RateReview />}
              color="secondary"
            />
          </Grid>
        </Grid>
      )}

      {/* Date Filter - Hide on GMB Insights tab (tab 0) */}
      {availableDates.length > 0 && activeTab !== 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Select Date</InputLabel>
            <Select
              value={selectedDate || ''}
              onChange={(e) => setSelectedDate(e.target.value)}
              label="Select Date"
            >
              <MenuItem value="">
                <em>Available Dates</em>
              </MenuItem>
              {availableDates.map((date) => (
                <MenuItem key={date} value={date}>
                   {date} ({formatDateToWords(date)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedDate && (
            <Chip
              label={`📍 Showing: ${selectedDate}`}
              onDelete={() => setSelectedDate(null)}
              color="primary"
              variant="outlined"
            />
          )}

          {/* Keyword Filter - Only on Keywords and Keyword Maps tabs (tabs 5 and 6) */}
          {(activeTab === 5 || activeTab === 6) && availableKeywords.length > 0 && (
            <>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Keyword</InputLabel>
                <Select
                  value={selectedKeyword || ''}
                  onChange={(e) => setSelectedKeyword(e.target.value)}
                  label="Select Keyword"
                >
                  <MenuItem value="">
                    <em>All Keywords</em>
                  </MenuItem>
                  {availableKeywords.map((keyword) => (
                    <MenuItem key={keyword} value={keyword}>
                       {keyword}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedKeyword && (
                <Chip
                  label={` Showing: ${selectedKeyword}`}
                  onDelete={() => setSelectedKeyword(null)}
                  color="success"
                  variant="outlined"
                />
              )}
            </>
          )}
        </Box>
      )}

      {/* Content Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="activity tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="GMB Insights" />
          <Tab label={`Bewertungen (${filterDataByDate(data.reviews, 'time').length|| 0})`} />
          <Tab label={`Medias (${filterDataByDate(data.medias, 'createTime').length|| 0})`} />
          <Tab label={`Posts (${filterPostsByDate(data.posts).length|| 0})`} />
          <Tab label={`Services (${data?.serviceActivities?.length || 0})`} />
          <Tab label={`Keywords (${filterKeywordsByKeyword(filterDataByDate(data?.keywords, 'analyzedAt'))?.length || 0})`} />
          <Tab label={`Keyword Ranking Maps (${filterKeywordsByKeyword(filterDataByDate(data?.keywords, 'analyzedAt'))?.length || 0})`} />
          
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Reviews Tab */}
          {activeTab === 1 && (
            <Box>
              {data?.reviews?.length > 0 ? (
                filterDataByDate(data.reviews, 'time').map(review => (
                  <ReviewCard key={review.id} review={review} />
                ))
              ) : (
                <Alert severity="info">
                  Keine Bewertungen im ausgewählten Zeitraum gefunden.
                </Alert>
              )}
            </Box>
          )}

         {/* Photos Tab */}
{activeTab === 2 && (
  <Box>
    {data.medias && data.medias.length > 0 ? (
      <Grid container spacing={2}>
        {filterDataByDate(data.medias, 'createTime')
          .filter(media => media.mediaFormat === 'PHOTO')
          .map((media, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={media.name || index}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea onClick={() => window.open(media.googleUrl || media.thumbnailUrl, '_blank')}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={media.googleUrl ||  media.thumbnailUrl}
                    alt={`Photo ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                      Erstellt: {media.createTime ? new Date(media.createTime).toLocaleDateString('de-DE') : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                      {media.dimensions?.widthPixels} × {media.dimensions?.heightPixels} px
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
      </Grid>
    ) : (
      <Alert severity="info">
        Keine media im ausgewählten Zeitraum gefunden.
      </Alert>
    )}
  </Box>
)}

          {/* Posts Tab */}
          {activeTab === 3 && (
            <Box>
              {data?.posts?.length > 0 ? (
                filterPostsByDate(data.posts).map(post => (
                  <PostCard key={post.id} post={post} />
                ))
              ) : (
                <Alert severity="info">
                  Keine Posts im ausgewählten Zeitraum gefunden.
                </Alert>
              )}
            </Box>
          )}

          {/* Services Tab */}
          {activeTab === 4 && (
            <ServiceActivityLog googleLocationId={selectedBusiness?.googleLocationId} />
          )}

{/* Keyword Tab - Simplified Pagination */}
{activeTab === 5 && (
  <Box>
    {data?.keywords?.length > 0 ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Simple Pagination Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {(() => {
              const filteredKeywords = filterKeywordsByKeyword(filterDataByDate(data.keywords, 'analyzedAt'));
              return `Zeigt ${Math.min((currentPage - 1) * pageSize + 1, filteredKeywords.length)}-${Math.min(currentPage * pageSize, filteredKeywords.length)} von ${filteredKeywords.length} Keywords`;
            })()}
          </Typography>
        </Box>

        {/* Keywords List */}
        {filterKeywordsByKeyword(filterDataByDate(data.keywords, 'analyzedAt'))
          .slice((currentPage - 1) * pageSize, currentPage * pageSize)
          .map((keyword, index) => (
            

             <Paper 
            key={index} 
            elevation={1} 
            sx={{ 
              p: 2, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {keyword.keyword}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Analyzed: {keyword.analyzedAt ? new Date(keyword.analyzedAt).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Best Rank
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {keyword.bestRank}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Visibility Score
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {keyword.visibilityScore}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Worst Rank
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {keyword.worstRank}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          ))
        }

        {/* Simple Pagination Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
          >
            Vorherige
          </Button>

          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            {(() => {
              const filteredKeywords = filterKeywordsByKeyword(filterDataByDate(data.keywords, 'analyzedAt'));
              return `Seite ${currentPage} von ${Math.ceil(filteredKeywords.length / pageSize)}`;
            })()}
          </Typography>

          <Button
            variant="outlined"
            disabled={(() => {
              const filteredKeywords = filterKeywordsByKeyword(filterDataByDate(data.keywords, 'analyzedAt'));
              return currentPage >= Math.ceil(filteredKeywords.length / pageSize);
            })()}
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            Nächste
          </Button>
        </Box>
      </Box>
    ) : (
      <Alert severity="info">
        Keine Keywords im ausgewählten Zeitraum gefunden.
      </Alert>
    )}
  </Box>
)}

          {/* Keyword Ranking Maps Tab - Grid Layout */}
          {activeTab === 6 && (
            <Box sx={{ p: 2 }}>
              {data?.keywords?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
  {/* All keywords within selected time range. Latest entry per keyword+date combination displayed. */}
  <Typography variant="h6" gutterBottom>
    Ranking Keyword Progress Maps ({data?.keywords?.length || 0} keywords)
  </Typography>
</Box>

                  {/* Keywords Grid */}
                  <Grid container spacing={2}>
                    {filterKeywordsByKeyword(filterDataByDate(data.keywords, 'analyzedAt')).map((keyword, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                          📍 {keyword.keyword}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Best Rank
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              #{keyword.bestRank}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Average Rank
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              #{keyword.averageRank
 || 'N/A'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Worst Rank
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              #{keyword.
worstRank

 || 'N/A'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Analyzed
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {keyword.analyzedAt ? new Date(keyword.analyzedAt).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Interactive Map - Test Locations */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                          Ranking Map ({keyword?.testLocations?.length || 0} locations)
                        </Typography>
                        <Paper
                          elevation={1}
                          sx={{
                            width: '100%',
                            height: 350,
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: '#f5f5f5'
                          }}
                        >
                          {keyword?.testLocations && keyword.testLocations.length > 0 && selectedBusiness ? (
                            <ErrorBoundary fallback={<Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>Map error. Locations: {keyword.testLocations.map(l => `${l.lat},${l.lng}`).join(' | ')}</Box>}>
                              <LocationMap
                                key={`${keyword.keyword}-${keyword.analyzedAt}`}
                                businessLocation={selectedBusiness}
                                keywords={[keyword]}
                              />
                            </ErrorBoundary>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                p: 2
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                {!keyword?.testLocations?.length ? 'No test locations' : 'Loading...'}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Box>

                     
                    </Paper>
                    </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : (
                <Alert severity="info">
                  Keine Keywords im ausgewählten Zeitraum gefunden.
                </Alert>
              )}
            </Box>
          )}

          {/* GMB Insights Time Series Chart Tab */}
          {activeTab === 0 && (
            <Box>
              {insightsChartData && insightsChartData.length > 0 ? (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                        KPI-Zeitverlauf
                      </Typography>
                    </Box>
                  </Box>

                  {/* Metric Filter */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight="bold" color="textSecondary">
                      Metriken anzeigen:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        icon={<Visibility />}
                        label="Einblendungen"
                        onClick={() => setVisibleMetrics(prev => ({
                          ...prev,
                          BUSINESS_IMPRESSIONS: !prev.BUSINESS_IMPRESSIONS
                        }))}
                        variant={visibleMetrics.BUSINESS_IMPRESSIONS ? 'filled' : 'outlined'}
                        sx={{
                          backgroundColor: visibleMetrics.BUSINESS_IMPRESSIONS ? '#2196f3' : 'transparent',
                          color: visibleMetrics.BUSINESS_IMPRESSIONS ? 'white' : '#2196f3',
                          borderColor: '#2196f3',
                          cursor: 'pointer'
                        }}
                      />
                      <Chip
                        icon={<Phone />}
                        label="Anrufe"
                        onClick={() => setVisibleMetrics(prev => ({
                          ...prev,
                          CALL_CLICKS: !prev.CALL_CLICKS
                        }))}
                        variant={visibleMetrics.CALL_CLICKS ? 'filled' : 'outlined'}
                        sx={{
                          backgroundColor: visibleMetrics.CALL_CLICKS ? '#4caf50' : 'transparent',
                          color: visibleMetrics.CALL_CLICKS ? 'white' : '#4caf50',
                          borderColor: '#4caf50',
                          cursor: 'pointer'
                        }}
                      />
                      <Chip
                        icon={<Language />}
                        label="Webseiten-Klicks"
                        onClick={() => setVisibleMetrics(prev => ({
                          ...prev,
                          WEBSITE_CLICKS: !prev.WEBSITE_CLICKS
                        }))}
                        variant={visibleMetrics.WEBSITE_CLICKS ? 'filled' : 'outlined'}
                        sx={{
                          backgroundColor: visibleMetrics.WEBSITE_CLICKS ? '#ff9800' : 'transparent',
                          color: visibleMetrics.WEBSITE_CLICKS ? 'white' : '#ff9800',
                          borderColor: '#ff9800',
                          cursor: 'pointer'
                        }}
                      />
                    </Box>
                  </Box>

                    {/* Summary Statistics */}
                  <Grid container spacing={2}>
                    {visibleMetrics.BUSINESS_IMPRESSIONS && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #2196f308 0%, #2196f315 100%)', border: '2px solid #2196f320' }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Visibility sx={{ color: '#2196f3' }} />
                              <Typography variant="body2" color="textSecondary">
                                Gesamt Einblendungen
                              </Typography>
                            </Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#2196f3' }}>
                              {insightsChartData.reduce((sum, day) => sum + (day.BUSINESS_IMPRESSIONS || 0), 0).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Ø {(insightsChartData.reduce((sum, day) => sum + (day.BUSINESS_IMPRESSIONS || 0), 0) / insightsChartData.length).toFixed(1)} pro Tag
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {visibleMetrics.CALL_CLICKS && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4caf5008 0%, #4caf5015 100%)', border: '2px solid #4caf5020' }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Phone sx={{ color: '#4caf50' }} />
                              <Typography variant="body2" color="textSecondary">
                                Gesamt Anrufe
                              </Typography>
                            </Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4caf50' }}>
                              {insightsChartData.reduce((sum, day) => sum + (day.CALL_CLICKS || 0), 0).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Ø {(insightsChartData.reduce((sum, day) => sum + (day.CALL_CLICKS || 0), 0) / insightsChartData.length).toFixed(1)} pro Tag
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {visibleMetrics.WEBSITE_CLICKS && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #ff980008 0%, #ff980015 100%)', border: '2px solid #ff980020' }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Language sx={{ color: '#ff9800' }} />
                              <Typography variant="body2" color="textSecondary">
                                Gesamt Webseiten-Klicks
                              </Typography>
                            </Box>
                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#ff9800' }}>
                              {insightsChartData.reduce((sum, day) => sum + (day.WEBSITE_CLICKS || 0), 0).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Ø {(insightsChartData.reduce((sum, day) => sum + (day.WEBSITE_CLICKS || 0), 0) / insightsChartData.length).toFixed(1)} pro Tag
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>

                  {/* Time Series Chart */}
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ width: '100%', height: 400 }}>
                      <ResponsiveContainer>
                        <LineChart data={insightsChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="formattedDate"
                            tick={{ fontSize: 12 }}
                            interval={insightsChartData.length > 7 ? Math.floor(insightsChartData.length / 7) : 0}
                          />
                          <YAxis />
                          <RechartsTooltip
                            formatter={(value) => {
                              return value?.toLocaleString() || 0;
                            }}
                          />
                          <Legend />
                          {visibleMetrics.BUSINESS_IMPRESSIONS && (
                            <Line
                              type="monotone"
                              dataKey="BUSINESS_IMPRESSIONS"
                              stroke="#2196f3"
                              strokeWidth={3}
                              dot={{ fill: '#2196f3', strokeWidth: 2, r: 5 }}
                              name="Einblendungen"
                            />
                          )}
                          {visibleMetrics.CALL_CLICKS && (
                            <Line
                              type="monotone"
                              dataKey="CALL_CLICKS"
                              stroke="#4caf50"
                              strokeWidth={3}
                              dot={{ fill: '#4caf50', strokeWidth: 2, r: 5 }}
                              name="Anrufe"
                            />
                          )}
                          {visibleMetrics.WEBSITE_CLICKS && (
                            <Line
                              type="monotone"
                              dataKey="WEBSITE_CLICKS"
                              stroke="#ff9800"
                              strokeWidth={3}
                              dot={{ fill: '#ff9800', strokeWidth: 2, r: 5 }}
                              name="Webseiten-Klicks"
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                
                </Box>
              ) : (
                <Alert severity="info">
                  Keine GMB Insights-Daten für den ausgewählten Zeitraum verfügbar.
                </Alert>
              )}
            </Box>
          )}

        </Box>
      </Paper>
    </Box>
  );
};

export default GMBActivity;




